<template>
  <div class="segmented-circle-border" :style="containerStyle">
    <svg :width="size" :height="size" :viewBox="`0 0 ${size} ${size}`">
      <!-- Draw segments -->
      <template v-if="segmentCount === 1">
        <!-- Single segment - full circle -->
        <circle
          :cx="center"
          :cy="center"
          :r="radius"
          fill="none"
          :stroke="hasUnseen ? 'url(#activeGradient)' : inactiveColor"
          :stroke-width="strokeWidth"
          stroke-linecap="round"
        />
      </template>
      <template v-else>
        <!-- Multiple segments -->
        <path
          v-for="(segment, index) in segments"
          :key="index"
          :d="segment.path"
          fill="none"
          :stroke="segment.isUnseen ? 'url(#activeGradient)' : inactiveColor"
          :stroke-width="strokeWidth"
          stroke-linecap="round"
        />
      </template>

      <!-- Gradient definition -->
      <defs>
        <linearGradient id="activeGradient" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" :stop-color="gradientStart" />
          <stop offset="100%" :stop-color="gradientEnd" />
        </linearGradient>
      </defs>
    </svg>

    <!-- Child content (avatar) -->
    <div class="segmented-circle-content" :style="contentStyle">
      <slot></slot>
    </div>
  </div>
</template>

<script setup>
import { computed } from "vue";

const props = defineProps({
  segmentCount: {
    type: Number,
    required: true,
    default: 1,
  },
  viewedCount: {
    type: Number,
    required: true,
    default: 0,
  },
  size: {
    type: Number,
    default: 60,
  },
  strokeWidth: {
    type: Number,
    default: 2.5,
  },
  gapSize: {
    type: Number,
    default: 5,
  },
  gradientStart: {
    type: String,
    default: "#00c444",
  },
  gradientEnd: {
    type: String,
    default: "#00e050",
  },
  inactiveColor: {
    type: String,
    default: "#b0b3b8",
  },
});

// Computed values
const center = computed(() => props.size / 2);
const radius = computed(() => (props.size - props.strokeWidth) / 2);
const hasUnseen = computed(() => props.viewedCount < props.segmentCount);

const containerStyle = computed(() => ({
  width: `${props.size}px`,
  height: `${props.size}px`,
  position: "relative",
}));

const contentStyle = computed(() => ({
  position: "absolute",
  top: "4px",
  left: "4px",
  right: "4px",
  bottom: "4px",
}));

// Calculate segment paths
const segments = computed(() => {
  if (props.segmentCount <= 1) return [];

  const result = [];
  const gapAngle = props.gapSize / radius.value; // Convert gap size to radians
  const totalGapAngle = gapAngle * props.segmentCount;
  const availableAngle = 2 * Math.PI - totalGapAngle;
  const segmentAngle = availableAngle / props.segmentCount;

  // Start from top (-90 degrees)
  let startAngle = -Math.PI / 2;

  for (let i = 0; i < props.segmentCount; i++) {
    const endAngle = startAngle + segmentAngle;

    // Calculate arc path
    const x1 = center.value + radius.value * Math.cos(startAngle);
    const y1 = center.value + radius.value * Math.sin(startAngle);
    const x2 = center.value + radius.value * Math.cos(endAngle);
    const y2 = center.value + radius.value * Math.sin(endAngle);

    // Large arc flag: 1 if angle > 180 degrees
    const largeArcFlag = segmentAngle > Math.PI ? 1 : 0;

    const path = `M ${x1} ${y1} A ${radius.value} ${radius.value} 0 ${largeArcFlag} 1 ${x2} ${y2}`;

    // Segments are unseen if their index >= viewedCount
    const isUnseen = i >= props.viewedCount;

    result.push({ path, isUnseen });

    startAngle = endAngle + gapAngle;
  }

  return result;
});
</script>

<style scoped>
.segmented-circle-border {
  display: inline-block;
}

.segmented-circle-border svg {
  position: absolute;
  top: 0;
  left: 0;
}

.segmented-circle-content {
  border-radius: 50%;
  overflow: hidden;
}
</style>
